import { InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Form, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { RetrieveImage } from 'app/components/pages/Extranet/commons/RetrieveImage/RetrieveImage';
import { SetupPackTag } from 'app/components/pages/Extranet/commons/SetupPackTag/SetupPackTag';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';
import { isPackInSetup } from 'app/utils/extranet';

import { usePackExperiences } from '../../../../../hooks';
import { ScoreTagComponent } from '../../../../Common/Score/_components/ScoreTag/ScoreTag';
import styles from '../ExperienceDiscount.module.scss';

type PackHeaderProps = {
  pack: ExtranetHotelPack;
  isExpanded: boolean;
  originalPackPrice: number;
  discountedPackPrice: number;
  discount?: number;
  score?: number;
  errorMessage?: string;
};

export const PackHeader = ({
  pack,
  isExpanded,
  originalPackPrice,
  discountedPackPrice,
  discount,
  score,
  errorMessage,
}: PackHeaderProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const form = Form.useFormInstance<{
    averageRoomPrice: number;
    defaultNightRoomDiscount: number;
  }>();

  const averageRoomPrice = Form.useWatch('averageRoomPrice', form);

  const { images } = usePackExperiences(pack);

  return (
    <div className={styles.packHeader}>
      <div className={styles.leftPart}>
        <RetrieveImage
          images={
            images.length > 0
              ? (images as [PictureView, PictureView, PictureView])
              : undefined
          }
        />
        <div className={styles.texts}>
          <p className={styles.title}>
            {pack.nameForHotel}
            {isPackInSetup(pack.status) && (
              <SetupPackTag isDaytime={pack.isDaytime} />
            )}
          </p>
          {averageRoomPrice && (
            <p className={styles.subtitle}>
              <span className={styles.roomDescription}>
                Average customer price
              </span>{' '}
              <span className={styles.priceWrapper}>
                <span className={styles.discountedPrice}>
                  {currencyFormatter(discountedPackPrice)}{' '}
                </span>
                <span className={styles.originalPrice}>
                  {currencyFormatter(originalPackPrice)}
                </span>
              </span>
              <span>
                <Tag>-{discount}%</Tag>
                <Tooltip title="Results of the reference room price and activities incentives">
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            </p>
          )}
        </div>
      </div>
      <div className={styles.rightPart}>
        {errorMessage ? (
          <span className={styles.errorMessage}>{errorMessage}</span>
        ) : (
          <ScoreTagComponent score={score} />
        )}
        <span
          className={classNames(styles.arrow, {
            [styles.expand]: isExpanded,
          })}
        >
          <RightOutlined />
        </span>
      </div>
    </div>
  );
};
